/**
 * How long to wait for a Supabase auth request before giving up on it.
 *
 * Long enough that a merely slow connection still succeeds, short enough that
 * a student is not left watching a spinner with no idea anything is wrong.
 */
export const AUTH_REQUEST_TIMEOUT_MS = 15_000;

/**
 * Only requests under this path get a deadline.
 *
 * Deliberately NOT a blanket timeout on every Supabase call. `/storage/v1/`
 * carries listening-audio uploads and `/functions/v1/` carries the PDF import,
 * both of which legitimately run for minutes — capping those would break real
 * features to fix a login bug. Auth calls, by contrast, are sub-second when the
 * service is healthy, so anything near the limit above is a hung request.
 */
const AUTH_PATH = '/auth/v1/';

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

/**
 * A `fetch` that puts a deadline on Supabase auth requests.
 *
 * supabase-js ships no timeout of any kind: its fetch layer sets no signal, so
 * a request that never comes back is waited on forever. When Supabase's API
 * gateway degrades (as in the Aug 2026 incident), that surfaces as a login
 * button that spins with no error and no way out, and as `getSession()` never
 * settling — which strands the app on its loading screen instead of reaching
 * the reconnecting screen that exists for exactly this case.
 *
 * Aborting is safe for the session: auth-js turns any non-Response failure into
 * an `AuthRetryableFetchError`, and it deliberately KEEPS the refresh token on
 * disk for retryable failures. A timeout therefore reports "could not reach the
 * server" and never invalidates a login.
 */
export function createAuthDeadlineFetch(
  underlying: typeof fetch = (input, init) => globalThis.fetch(input, init),
  timeoutMs: number = AUTH_REQUEST_TIMEOUT_MS,
): typeof fetch {
  return async function fetchWithAuthDeadline(input, init) {
    if (!requestUrl(input).includes(AUTH_PATH)) {
      return underlying(input, init);
    }

    const controller = new AbortController();
    const abortAsTimeout = (): void => {
      controller.abort(new DOMException('Auth request timed out', 'TimeoutError'));
    };
    const timer = setTimeout(abortAsTimeout, timeoutMs);

    // Never drop a caller's own cancellation on the floor by replacing its
    // signal with ours — chain it through instead.
    const caller = init?.signal ?? null;
    if (caller !== null) {
      if (caller.aborted) {
        controller.abort(caller.reason);
      } else {
        caller.addEventListener('abort', () => controller.abort(caller.reason), { once: true });
      }
    }

    try {
      return await underlying(input, { ...init, signal: controller.signal });
    } finally {
      clearTimeout(timer);
    }
  };
}
